import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useSearchParams } from "react-router"
import { usePrevious } from "@uidotdev/usehooks"
import { graphql } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { useUserData } from "@/hooks/useUserData"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { requestTakeOverDialog_patientFileLockFragment$key } from "@/modules/common/dialog/__generated__/requestTakeOverDialog_patientFileLockFragment.graphql"
import { useRequestTakeOverDialog } from "@/modules/common/dialog/useRequestTakeOverDialog"
import { useMedicalRecordDialogStore } from "@/modules/patients/detail/store/useCloseMedicalRecordDialogStore"
import { PatientFileLockAction } from "@/modules/patients/services/__generated__/usePatientFileLockChangeSubscription.graphql"
import {
  PatientFileLockTakeOverResult,
  usePatientFileLockChangeSubscription,
} from "@/modules/patients/services/usePatientFileLockChangeSubscription"
import { usePatientFileLockQuery } from "@/modules/patients/services/usePatientFileLockQuery"

type RequestTakeOverDialogProps = {
  patientId: string
  patientName: string
  onPatientFileLockTakeOverEnded?: (result: PatientFileLockTakeOverResult) => void
  handleConfirmationClose?: () => void
}

type ConfirmationDialog = {
  open: boolean
  title: string
}

type PatientFileLockChange = {
  action: PatientFileLockAction
  takeOverRequestCountdownValue: number | null
}

export const PatientFileLockFragment = graphql`
  fragment requestTakeOverDialog_patientFileLockFragment on PatientFileLock {
    id
    patientId
    lockedBy
    lockedAt
    lockRequestedAt
    lockRequestedBy
    appointment {
      id
      takenAt
      startingSoonNotificationSent
      status
      isCompleted
      chatDetails {
        chatThreadId
      }
    }
  }
`

const RequestTakeOverDialog = ({
  patientId,
  patientName,
  onPatientFileLockTakeOverEnded,
  handleConfirmationClose,
}: RequestTakeOverDialogProps) => {
  const { userName } = useUser()
  const [t] = useTranslation()
  const [searchParams] = useSearchParams()
  const isInboundCall = getInsensitiveSearchParams(searchParams, "inbound-call").exists

  const [openDialog, setOpenDialog] = useState(false)
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialog>({
    open: false,
    title: "",
  })

  const [status, setStatus] = useState<PatientFileLockAction>()
  const [patientFileLockChange, setPatientFileLockChange] = useState<PatientFileLockChange>()

  usePatientFileLockChangeSubscription(patientId, setPatientFileLockChange, onPatientFileLockTakeOverEnded)

  const patientFileLock = usePatientFileLockQuery(patientId)

  const patientFileLockFragment = useFragment<requestTakeOverDialog_patientFileLockFragment$key>(
    PatientFileLockFragment,
    patientFileLock,
  )

  const setCloseConfirmationDialog = useMedicalRecordDialogStore((state) => state.setCloseConfirmationDialog)
  const { description, title, actions } = useRequestTakeOverDialog(
    status,
    patientFileLockChange,
    patientName,
    patientId,
    patientFileLockFragment,
    isInboundCall,
  )
  const patientFileLockInformation = useMemo(() => {
    return {
      status,
      patientFileLockFragment,
    }
  }, [status, patientFileLockFragment])

  const action = patientFileLockChange?.action
  const lockedBy = patientFileLockInformation.patientFileLockFragment?.lockedBy
  const lockRequestedBy = patientFileLockInformation.patientFileLockFragment?.lockRequestedBy

  const { firstName: lockByFirstName, lastName: lockByLastName } = useUserData(lockedBy) || {}
  const lockedByName = `${lockByFirstName} ${lockByLastName}`

  const previousPatientFileLockInformation = usePrevious(patientFileLockInformation)
  const previousLockedBy = previousPatientFileLockInformation?.patientFileLockFragment?.lockedBy
  const previousLockRequestedBy = previousPatientFileLockInformation?.patientFileLockFragment?.lockRequestedBy

  if (openDialog) {
    setCloseConfirmationDialog(false)
  }

  const processConfirmationDialog = useCallback(
    (lockedByName: string, status: PatientFileLockAction) => {
      let open = false
      let title = ""

      switch (status) {
        case "ALLOWED":
          open = true
          title = t("patient.takeOver.ALLOWED.title", { lockedBy: lockedByName })
          break
        case "DENIED":
          open = true
          title = t("patient.takeOver.DENIED.title", { lockedBy: lockedByName })
          break
        case "CANCEL_REQUEST":
        case "RELEASED":
        case "REQUESTED":
        case "REQUEST_COUNTDOWN":
        case "TAKEN":
          break
      }

      setConfirmationDialog({ open, title })
    },
    [t, setConfirmationDialog],
  )

  useEffect(() => {
    if (action === "REQUEST_COUNTDOWN") {
      if (userName === lockRequestedBy) {
        setOpenDialog(true)
        setStatus("REQUESTED")
      } else if (userName === lockedBy) {
        setOpenDialog(true)
        setStatus("REQUEST_COUNTDOWN")
      }
    }

    if (action === "TAKEN" || action === "ALLOWED") {
      if (userName === previousLockedBy && lockedBy !== previousLockedBy) {
        setStatus("ALLOWED")
        processConfirmationDialog(lockedByName, "ALLOWED")
        setOpenDialog(false)
        if (isInboundCall) {
          window.close()
        }
      } else if (userName === previousLockRequestedBy) {
        setOpenDialog(false)
      }
    }

    if (action === "DENIED") {
      if (userName === previousLockRequestedBy) {
        setStatus("DENIED")
        processConfirmationDialog(lockedByName, "DENIED")
        setOpenDialog(false)
      } else if (userName === lockedBy) {
        setOpenDialog(false)
      }
    }

    if (action === "CANCEL_REQUEST") {
      setOpenDialog(false)
    }
  }, [
    action,
    userName,
    lockedBy,
    previousLockedBy,
    lockRequestedBy,
    previousLockRequestedBy,
    lockedByName,
    isInboundCall,
    processConfirmationDialog,
  ])

  return (
    <>
      <Dialog
        open={openDialog}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            const closeAction = actions.find((v) => v.action === "close")
            if (closeAction) {
              closeAction.handler()
            }
          }
          setOpenDialog(isOpen)
        }}>
        <DialogPortal>
          <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            variant="small"
            data-testid="request-dialog-content">
            <DialogHeader>
              <DialogTitle data-testid="request-dialog-title">{title}</DialogTitle>
            </DialogHeader>
            <div className="px-6 py-2">
              <label className="text-on-surface-subdued text-sm" data-testid="request-dialog-description">
                {description}
              </label>
            </div>
            <DialogFooter className="flex gap-2 p-6 pt-4">
              {actions.map((button) => (
                <Button
                  key={button.label}
                  variant={button.variant}
                  onClick={() => button.handler()}
                  data-testid={`request-dialog-action-${button.action}`}>
                  {button.label}
                </Button>
              ))}
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <Dialog
        open={confirmationDialog.open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleConfirmationClose?.()
          }
          setConfirmationDialog({ ...confirmationDialog, open: isOpen })
        }}>
        <DialogPortal>
          <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            variant="small"
            data-testid="confirmation-dialog-content">
            <DialogHeader>
              <DialogTitle data-testid="confirmation-dialog-title">{confirmationDialog.title}</DialogTitle>
            </DialogHeader>
            <DialogFooter className="flex gap-2 p-6 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  handleConfirmationClose?.()
                  setConfirmationDialog({ ...confirmationDialog, open: false })
                }}
                data-testid="confirmation-dialog-close">
                {t("common.button.close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  )
}

export { RequestTakeOverDialog, type PatientFileLockChange }
